/**
 * WhatsApp-style notification sounds for incoming calls and messages.
 * Uses Web Audio API to synthesize distinctive tones without audio file assets.
 */

let messageAudioCtx: AudioContext | null = null
let callAudioCtx: AudioContext | null = null
let callIntervalId: ReturnType<typeof setInterval> | null = null
let callOscillators: { o1: OscillatorNode; o2: OscillatorNode; g: GainNode } | null = null

/**
 * WhatsApp-style message notification: two quick ascending tones.
 * Plays once and stops — suitable for new message alerts.
 */
export function playWhatsAppMessageSound(): void {
  try {
    // Reuse or create AudioContext
    if (!messageAudioCtx || messageAudioCtx.state === 'closed') {
      messageAudioCtx = new AudioContext()
    }
    const ctx = messageAudioCtx

    // Resume if suspended (autoplay policy)
    if (ctx.state === 'suspended') {
      void ctx.resume()
    }

    // First tone (lower)
    const o1 = ctx.createOscillator()
    const g1 = ctx.createGain()
    o1.type = 'sine'
    o1.frequency.setValueAtTime(523.25, ctx.currentTime) // C5
    g1.gain.setValueAtTime(0, ctx.currentTime)
    g1.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.02)
    g1.gain.setValueAtTime(0.12, ctx.currentTime + 0.08)
    g1.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.12)
    o1.connect(g1)
    g1.connect(ctx.destination)
    o1.start(ctx.currentTime)
    o1.stop(ctx.currentTime + 0.12)

    // Second tone (higher) — starts after first
    const o2 = ctx.createOscillator()
    const g2 = ctx.createGain()
    o2.type = 'sine'
    o2.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1) // E5
    g2.gain.setValueAtTime(0, ctx.currentTime + 0.1)
    g2.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.12)
    g2.gain.setValueAtTime(0.12, ctx.currentTime + 0.18)
    g2.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.22)
    o2.connect(g2)
    g2.connect(ctx.destination)
    o2.start(ctx.currentTime + 0.1)
    o2.stop(ctx.currentTime + 0.22)
  } catch {
    // Audio playback can fail silently — never break the notification flow
  }
}

/**
 * WhatsApp-style incoming call ringtone: repeating dual-tone pattern.
 * Loops until stopWhatsAppCallSound() is called.
 * Uses US standard ring cadence (2s on, 4s off) with WhatsApp-like frequencies.
 */
export function playWhatsAppCallSound(): void {
  try {
    // Already ringing
    if (callIntervalId) return

    if (!callAudioCtx || callAudioCtx.state === 'closed') {
      callAudioCtx = new AudioContext()
    }
    const ctx = callAudioCtx

    if (ctx.state === 'suspended') {
      void ctx.resume()
    }

    const ringBurst = (): void => {
      if (!callAudioCtx) return
      const ctx = callAudioCtx

      const o1 = ctx.createOscillator()
      const o2 = ctx.createOscillator()
      const g = ctx.createGain()

      // WhatsApp-style frequencies (slightly different from standard US ring)
      o1.type = 'sine'
      o1.frequency.setValueAtTime(440, ctx.currentTime) // A4
      o2.type = 'sine'
      o2.frequency.setValueAtTime(480, ctx.currentTime) // slightly sharp A4

      // Envelope: fade in, hold, fade out over 2 seconds
      g.gain.setValueAtTime(0, ctx.currentTime)
      g.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.05)
      g.gain.setValueAtTime(0.1, ctx.currentTime + 1.95)
      g.gain.linearRampToValueAtTime(0, ctx.currentTime + 2.0)

      o1.connect(g)
      o2.connect(g)
      g.connect(ctx.destination)

      o1.start(ctx.currentTime)
      o2.start(ctx.currentTime)
      o1.stop(ctx.currentTime + 2.0)
      o2.stop(ctx.currentTime + 2.0)

      callOscillators = { o1, o2, g }
    }

    // Start immediately, then repeat every 6 seconds (2s ring + 4s silence)
    ringBurst()
    callIntervalId = setInterval(ringBurst, 6000)
  } catch {
    // Audio playback can fail silently
  }
}

/**
 * Stops the incoming call ringtone.
 */
export function stopWhatsAppCallSound(): void {
  try {
    if (callIntervalId) {
      clearInterval(callIntervalId)
      callIntervalId = null
    }

    if (callOscillators && callAudioCtx) {
      const { o1, o2, g } = callOscillators
      const ctx = callAudioCtx
      // Quick fade out to avoid click
      g.gain.setValueAtTime(g.gain.value, ctx.currentTime)
      g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05)
      o1.stop(ctx.currentTime + 0.05)
      o2.stop(ctx.currentTime + 0.05)
      callOscillators = null
    }

    if (callAudioCtx && callAudioCtx.state !== 'closed') {
      void callAudioCtx.close()
      callAudioCtx = null
    }
  } catch {
    // Ignore cleanup errors
  }
}
